import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMsal } from "@azure/msal-react";
import { useEffect } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useColorScheme } from "@mui/material/styles";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import LightModeIcon from "@mui/icons-material/LightMode";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import { useMain } from "../state/userMainState";
import { useLogoImage } from "../state/logoImageState";

export default function UserMenu() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { mode, setMode } = useColorScheme();
  const activeAccount = instance.getActiveAccount();
  const { data } = useMain();
  const user = data?.data?.data?.data?.data?.user;
  const companyContactNo = user?.companyContactNo;

  const { data: logoImageData, isLoading } = useLogoImage(companyContactNo);
  const [objectUrl, setObjectUrl] = useState();
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (!(logoImageData instanceof Blob)) {
      setObjectUrl(undefined);
      return undefined;
    }
    const url = URL.createObjectURL(logoImageData);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [logoImageData]);

  if (!activeAccount) return null;

  const close = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{ p: 0.5 }}
          aria-label="Account menu"
          aria-haspopup="menu"
          aria-expanded={Boolean(anchorEl)}
        >
          <Avatar
            src={objectUrl}
            sx={{
              width: 32,
              height: 32,
              opacity: isLoading ? 0.7 : 1,
            }}
          >
            {companyContactNo?.charAt(0)?.toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {user?.name}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {activeAccount.username}
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          selected={mode === "system"}
          onClick={() => setMode("system")}
        >
          <ListItemIcon>
            <SettingsBrightnessIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>System</ListItemText>
        </MenuItem>
        <MenuItem selected={mode === "light"} onClick={() => setMode("light")}>
          <ListItemIcon>
            <LightModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Light</ListItemText>
        </MenuItem>
        <MenuItem selected={mode === "dark"} onClick={() => setMode("dark")}>
          <ListItemIcon>
            <DarkModeIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dark</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            close();
            navigate({ to: "/account" });
          }}
        >
          <ListItemIcon>
            <ManageAccountsRoundedIcon fontSize="small" />
          </ListItemIcon>
          Account settings
        </MenuItem>
        <MenuItem
          onClick={() => {
            close();
            instance.logoutRedirect();
          }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
